import { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui';
import { api } from '@/lib/api';

// Fix Leaflet default marker icons broken by Vite's asset bundling
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

async function reverseGeocode(lat: number, lng: number): Promise<string> {
  try {
    const data = await api.reverseGeocode(lat, lng);
    const addr = data.address ?? {};
    const name = data.name || addr.amenity || addr.tourism || addr.historic || addr.shop || addr.leisure;
    const road = addr.road;
    const city = addr.city || addr.town || addr.village || addr.municipality || addr.county;
    const state = addr.state;
    const country = addr.country;
    const parts = [name, road, city, state, country].filter(Boolean);
    if (parts.length > 0) return parts.join(', ');
    return data.display_name?.trim() ?? '';
  } catch {
    return '';
  }
}

interface LocationPickerProps {
  initialLocation?: { latitude: number; longitude: number; locationName: string };
  referenceDate?: string;
  onSave: (location: { latitude: number; longitude: number; locationName: string }) => void;
  onCancel: () => void;
}

function MapUpdater({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => {
    if (!isNaN(lat) && !isNaN(lng)) {
      map.setView([lat, lng], map.getZoom(), { animate: true, duration: 0.15 });
    }
  }, [lat, lng, map]);
  return null;
}

function MapClickHandler({ onMapClick }: { onMapClick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

export function LocationPicker({ initialLocation, referenceDate, onSave, onCancel }: LocationPickerProps) {
  const { t } = useTranslation();
  const [lat, setLat] = useState(initialLocation?.latitude ?? 0);
  const [lng, setLng] = useState(initialLocation?.longitude ?? 0);
  const [locationName, setLocationName] = useState(initialLocation?.locationName ?? '');
  const [latInput, setLatInput] = useState(String(initialLocation?.latitude ?? ''));
  const [lngInput, setLngInput] = useState(String(initialLocation?.longitude ?? ''));
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [isCopyingPrevious, setIsCopyingPrevious] = useState(false);
  const [copyPreviousError, setCopyPreviousError] = useState<string | null>(null);

  const applyCoords = useCallback(async (latitude: number, longitude: number) => {
    setLat(latitude);
    setLng(longitude);
    setLatInput(String(latitude));
    setLngInput(String(longitude));
    setIsGeocoding(true);
    const name = await reverseGeocode(latitude, longitude);
    if (name) setLocationName(name);
    setIsGeocoding(false);
  }, []);

  useEffect(() => {
    if (!initialLocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          void applyCoords(pos.coords.latitude, pos.coords.longitude);
        },
        () => {
          // Geolocation denied or unavailable — use default coords
        }
      );
    }
  }, [initialLocation, applyCoords]);

  const handleLatChange = (value: string) => {
    setLatInput(value);
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) setLat(parsed);
  };

  const handleLngChange = (value: string) => {
    setLngInput(value);
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) setLng(parsed);
  };

  const handleSave = () => {
    onSave({ latitude: lat, longitude: lng, locationName });
  };

  const handleCopyFromPreviousDay = async () => {
    setIsCopyingPrevious(true);
    setCopyPreviousError(null);
    try {
      const previousDay = new Date(referenceDate ?? Date.now());
      previousDay.setDate(previousDay.getDate() - 1);
      const start = new Date(previousDay);
      start.setHours(0, 0, 0, 0);
      const end = new Date(previousDay);
      end.setHours(23, 59, 59, 999);

      const result = await api.listEntries({
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        hasLocation: 'true',
        limit: '1',
      });
      const previousEntry = result.items[0];
      if (previousEntry?.locationLatitude != null && previousEntry?.locationLongitude != null) {
        setLat(previousEntry.locationLatitude);
        setLng(previousEntry.locationLongitude);
        setLatInput(String(previousEntry.locationLatitude));
        setLngInput(String(previousEntry.locationLongitude));
        setLocationName(previousEntry.locationName ?? '');
      } else {
        setCopyPreviousError(t('entries.noPreviousDayLocation'));
      }
    } catch {
      setCopyPreviousError(t('entries.noPreviousDayLocation'));
    } finally {
      setIsCopyingPrevious(false);
    }
  };

  const validCoords = !isNaN(lat) && !isNaN(lng);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl rounded border border-paper-300 bg-paper-50 shadow-sm">
        <div className="p-4 border-b border-paper-300">
          <h2 className="text-base font-semibold text-ink">
            {t('entries.setLocation')}
          </h2>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <Button
              variant="secondary"
              onClick={() => { void handleCopyFromPreviousDay(); }}
              isLoading={isCopyingPrevious}
              className="w-full"
            >
              {t('entries.copyFromPreviousDay')}
            </Button>
            {copyPreviousError && (
              <p className="mt-1 text-xs text-red-600">{copyPreviousError}</p>
            )}
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-ink-500 mb-1">
                {t('entries.latitude')}
              </label>
              <input
                type="number"
                value={latInput}
                onChange={(e) => handleLatChange(e.target.value)}
                step="any"
                className="input py-1.5 text-sm"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-ink-500 mb-1">
                {t('entries.longitude')}
              </label>
              <input
                type="number"
                value={lngInput}
                onChange={(e) => handleLngChange(e.target.value)}
                step="any"
                className="input py-1.5 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-500 mb-1">
              {t('entries.locationName')}
            </label>
            <div className="relative">
              <input
                type="text"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                maxLength={200}
                className="input py-1.5 text-sm"
              />
              {isGeocoding && (
                <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                  <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-paper-400 border-t-transparent" />
                </div>
              )}
            </div>
          </div>

          {validCoords && (
            <MapContainer
              center={[lat, lng]}
              zoom={13}
              className="h-96 w-full rounded-md z-0"
              style={{ zIndex: 0 }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <Marker position={[lat, lng]} />
              <MapUpdater lat={lat} lng={lng} />
              <MapClickHandler onMapClick={(clickLat, clickLng) => { void applyCoords(clickLat, clickLng); }} />
            </MapContainer>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-paper-300">
          <Button variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!validCoords}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
